import {produce} from 'immer';
import {isEqual, sortBy} from 'lodash';
import {useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';
import {Button} from '@/components/Button';
import {Chip} from '@/components/Chip';
import toast from '@/components/HotToast';
import {CheckIconComponent, ChevronRightIconComponent} from '@/components/Icons';
import {Modal} from '@/components/Modal';
import {Typography, typographyStyle} from '@/components/Typography';
import {KODATA_MAP_CATEGORIES} from '@/constants';
import {KodataCategory} from '@/enums';
import {KodataCategoryGroup} from '@/map/@types';
import {isValidKodataCategory} from '@/map/utils';
import {Flexbox, IconClose, interceptProps, PaddingBox, Section} from '@/styled-components';
import {palette} from '@/styles';

interface IProps {
  defaultOptions?: KodataCategoryGroup[];
  loading?: boolean;
  maxCount?: number;
  minCount?: number;
  note?: string[];
  onComplete?(items: KodataCategoryGroup[]): void;
  onClose?(): void;
}

const MAX_COUNT = 6;
const cat1depth = Object.values(KodataCategory);
const cat1depthElementIdPrefix = 'sb-1depth-item-';

export const CategorySelectWindow = ({
  defaultOptions,
  loading,
  maxCount = MAX_COUNT,
  minCount,
  onComplete,
  onClose,
}: IProps) => {
  const [active1depth, setActive1Depth] = useState<KodataCategory>();
  const [selected, setSelected] = useState<KodataCategoryGroup[]>([]);

  const cat2depth = useMemo<KodataCategoryGroup[]>(() => {
    if (!active1depth) return [];

    const cat: string[] = KODATA_MAP_CATEGORIES[active1depth] ?? [];

    return cat.sort().map((i) => ({
      category: active1depth,
      categoryDetail: i,
    }));
  }, [active1depth]);

  const onSelected = (cat: KodataCategoryGroup) => () => {
    const index = selected.findIndex((i) => isEqual(i, cat));

    if (index === -1 && selected.length + 1 > maxCount) {
      if (maxCount === 1) {
        return setSelected([cat]);
      } else {
        return toast(`업종은 ${maxCount}개까지 선택할 수 있습니다.`);
      }
    }

    setSelected(
      produce((draft) => {
        if (index === -1) {
          draft.push(cat);
        } else {
          draft.splice(index, 1);
        }
      })
    );
  };

  const onClickGroup = (group: KodataCategory) => () => setActive1Depth(group);

  const handleComplete = () => {
    if (loading) {
      return false;
    } else if (!selected?.length && minCount !== 0) {
      return false;
    }

    if (!selected.length) {
      onComplete?.([]);
    } else if (isEqual(sortBy(selected), sortBy(defaultOptions))) {
      onClose?.();
    } else {
      onComplete?.(selected);
    }
  };

  useEffect(() => {
    if (!selected?.length && defaultOptions?.length) {
      const next = defaultOptions
        .map((i) => {
          if (isValidKodataCategory(i)) {
            return i;
          }
          return undefined;
        })
        .filter((i) => !!i);

      setSelected(next as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOptions]);

  return (
    <Modal
      width={372}
      title={`업종 선택${
        maxCount > 1 ? `(${selected?.length}/${maxCount})` : ''
      }`}
      onClose={onClose}
    >
      <PaddingBox spacing={16}>
        <Options>
          <div>
            <div>
              {cat1depth.map((i, idx) => (
                <MainItem
                  id={`${cat1depthElementIdPrefix}${idx}`}
                  key={i}
                  onClick={onClickGroup(i)}
                  active={i === active1depth}
                >
                  {i}
                  {i === active1depth && (
                    <ChevronRightIconComponent color={palette.textPrimary} />
                  )}
                </MainItem>
              ))}
            </div>
          </div>
          <div>
            <div>
              {!!(active1depth && cat2depth?.length) &&
                cat2depth.map((i) => {
                  const active =
                    selected.findIndex((s) => isEqual(s, i)) !== -1;
                  return (
                    <SubItem
                      key={`${i.category}-${i.categoryDetail}`}
                      active={active}
                      onClick={onSelected(i)}
                    >
                      {i.categoryDetail}
                      {active && (
                        <CheckIconComponent color={palette.primary500} />
                      )}
                    </SubItem>
                  );
                })}
            </div>
          </div>
        </Options>
        {selected?.length > 0 ? (
          <SelectedBox>
            {selected.map((i) => (
              <Chip key={`${i.category}-${i.categoryDetail}`}>
                <Typography color={palette.gray500}>{i.category} ∙ </Typography>
                <Typography color={palette.gray800}>
                  {i.categoryDetail}
                </Typography>
                <IconClose onClick={onSelected(i)} />
              </Chip>
            ))}
          </SelectedBox>
        ) : (
          <Section spacing={4}>
            <Typography variant="medium14" color={palette.gray600}>
              조회할 업종을 선택해주세요.
            </Typography>
          </Section>
        )}
        <Section spacing={4}>
          <Button
            variant="fill"
            size="medium"
            color="secondary"
            fullWidth
            disabled={loading}
            onClick={handleComplete}
          >
            {loading ? '로드 중...' : '적용'}
          </Button>
        </Section>
      </PaddingBox>
    </Modal>
  );
};

const Options = styled.div`
  display: flex;
  height: 308px;
  border: solid 1px ${palette.gray200};
  overflow: hidden;
  border-radius: 2px;
  & > div {
    overflow: auto;
    flex-grow: 1;
    flex-basis: 0;
  }
  & > div:first-of-type {
    background-color: ${palette.gray100};
  }
`;

const Item = styled(Flexbox).withConfig({
  shouldForwardProp: interceptProps(['active']),
})<{
  active: boolean;
}>`
  border-top: solid 1px transparent;
  border-bottom: solid 1px transparent;
  justify-content: space-between;
  padding: 10px 16px;
  cursor: pointer;
  gap: 8px;
  color: ${palette.textPrimary};
  ${{...typographyStyle.medium14}}
  & > * {
    flex-shrink: 0;
  }
`;

const MainItem = styled(Item)`
  ${({active}) =>
    active
      ? `
    font-weight: 700;
    border-top: solid 1px ${palette.gray200};
    border-bottom: solid 1px ${palette.gray200};
    background-color: ${palette.white};
    &:first-of-type {
      border-top: solid 1px transparent;
    }
    &:last-of-type {
      border-bottom: solid 1px transparent;
    }
  `
      : `
    border-right: solid 1px ${palette.gray200};
  `}
`;

const SubItem = styled(Item)`
  ${({active}) =>
    active
      ? `
    font-weight: 700;
    color: ${palette.primary500};
    background-color: ${palette.primary100};
  `
      : `
    &:hover {
      background-color: ${palette.gray100};
    }
  `}
`;

const SelectedBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;
